import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Plus, Trash2, X, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { email: "", password: "", name: "", role: "collector", phone: "", extension: "", can_view_all_reports: false };

export default function Users() {
    const { user: me } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null); // null = create mode, id = edit mode
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/users");
            setRows(r.data);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    function openCreate() {
        setEditId(null);
        setForm(EMPTY);
        setOpen(true);
    }

    function openEdit(u) {
        setEditId(u.id);
        setForm({
            email: u.email,
            password: "",
            name: u.name || "",
            role: u.role,
            phone: u.phone || "",
            extension: u.extension || "",
            can_view_all_reports: !!u.can_view_all_reports,
        });
        setOpen(true);
    }

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            if (editId) {
                const payload = {
                    name: form.name,
                    role: form.role,
                    phone: form.phone,
                    extension: form.extension,
                    can_view_all_reports: form.can_view_all_reports,
                };
                if (form.password) payload.password = form.password;
                await api.put(`/users/${editId}`, payload);
                toast.success("Kullanıcı güncellendi");
            } else {
                await api.post("/users", {
                    email: form.email,
                    password: form.password,
                    name: form.name,
                    role: form.role,
                    phone: form.phone,
                    extension: form.extension,
                });
                toast.success("Kullanıcı eklendi");
            }
            setOpen(false);
            setForm(EMPTY);
            setEditId(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    async function del(id) {
        if (!window.confirm("Kullanıcı silinsin mi?")) return;
        try {
            await api.delete(`/users/${id}`);
            toast.success("Silindi");
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    }

    async function toggleReports(u) {
        try {
            await api.put(`/users/${u.id}`, { can_view_all_reports: !u.can_view_all_reports });
            toast.success(u.can_view_all_reports ? "Tüm rapor yetkisi kaldırıldı" : "Tüm rapor yetkisi verildi");
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    }

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6 flex items-center justify-between">
                <div>
                    <div className="overline">Bölüm 04</div>
                    <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">Kullanıcılar</h1>
                </div>
                <button data-testid="new-user-button" onClick={openCreate} className="btn-sharp">
                    <Plus size={14} /> Yeni Kullanıcı
                </button>
            </header>

            <div className="px-10 py-8 overflow-x-auto">
                {loading ? (
                    <div className="font-mono text-xs uppercase text-neutral-500">Yükleniyor…</div>
                ) : (
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead>
                            <tr className="border-b border-black/10">
                                <th className="overline text-left py-3">Ad</th>
                                <th className="overline text-left py-3">E-posta</th>
                                <th className="overline text-left py-3">Telefon / Dahili</th>
                                <th className="overline text-left py-3">Rol</th>
                                <th className="overline text-left py-3">Tüm Rapor Yetkisi</th>
                                <th className="overline text-left py-3">Oluşturulma</th>
                                <th className="w-16"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.id} className="border-b border-black/5">
                                    <td className="py-3">{u.name || "-"}</td>
                                    <td className="py-3 font-mono text-xs">{u.email}</td>
                                    <td className="py-3 font-mono text-xs text-neutral-600">
                                        {u.phone || "—"}
                                        {u.extension ? <span className="text-neutral-400"> · dh. {u.extension}</span> : null}
                                    </td>
                                    <td className="py-3">
                                        <span className="inline-flex items-center gap-1 border border-black/20 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest">
                                            {u.role === "admin" && <ShieldCheck size={11} />}
                                            {u.role === "admin" ? "Yönetici" : "Tahsilatçı"}
                                        </span>
                                    </td>
                                    <td className="py-3">
                                        {u.role === "collector" ? (
                                            <button
                                                data-testid={`toggle-reports-${u.id}`}
                                                onClick={() => toggleReports(u)}
                                                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition ${
                                                    u.can_view_all_reports
                                                        ? "bg-brand-green/10 text-brand-green border-brand-green/40"
                                                        : "bg-neutral-50 text-neutral-500 border-black/15 hover:border-brand-blue"
                                                }`}
                                            >
                                                {u.can_view_all_reports ? "Açık" : "Kapalı"}
                                            </button>
                                        ) : (
                                            <span className="text-[10px] font-mono uppercase text-neutral-400">n/a</span>
                                        )}
                                    </td>
                                    <td className="py-3 font-mono text-xs text-neutral-500">
                                        {formatDateTime(u.created_at)}
                                    </td>
                                    <td className="py-3 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                data-testid={`edit-user-${u.id}`}
                                                onClick={() => openEdit(u)}
                                                className="text-neutral-400 hover:text-brand-blue"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            {me?.id !== u.id && (
                                                <button
                                                    data-testid={`del-user-${u.id}`}
                                                    onClick={() => del(u.id)}
                                                    className="text-neutral-400 hover:text-brand-red"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
                    <form
                        data-testid="user-form"
                        onSubmit={submit}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-black/15 w-full max-w-lg p-8 animate-fade-up"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-display text-2xl font-bold tracking-tight">
                                {editId ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}
                            </h2>
                            <button type="button" onClick={() => setOpen(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <label className="block col-span-2">
                                <span className="overline block mb-2">Ad Soyad *</span>
                                <input
                                    data-testid="uf-name"
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                                />
                            </label>
                            <label className="block col-span-2">
                                <span className="overline block mb-2">E-posta {editId ? "(değiştirilemez)" : "*"}</span>
                                <input
                                    data-testid="uf-email"
                                    type="email"
                                    required={!editId}
                                    disabled={!!editId}
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue disabled:bg-neutral-100 disabled:text-neutral-500"
                                />
                            </label>
                            <label className="block">
                                <span className="overline block mb-2">Telefon</span>
                                <input
                                    data-testid="uf-phone"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    placeholder="0532 000 00 00"
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue placeholder:text-neutral-400"
                                />
                            </label>
                            <label className="block">
                                <span className="overline block mb-2">Dahili</span>
                                <input
                                    data-testid="uf-extension"
                                    value={form.extension}
                                    onChange={(e) => setForm({ ...form, extension: e.target.value })}
                                    placeholder="1234"
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue placeholder:text-neutral-400"
                                />
                            </label>
                            <label className="block col-span-2">
                                <span className="overline block mb-2">
                                    Şifre {editId ? "(boş bırakılırsa değişmez)" : "*"}
                                </span>
                                <input
                                    data-testid="uf-password"
                                    type="password"
                                    required={!editId}
                                    minLength={editId ? undefined : 6}
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                                />
                            </label>
                            <label className="block col-span-2">
                                <span className="overline block mb-2">Rol</span>
                                <select
                                    data-testid="uf-role"
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue bg-white"
                                >
                                    <option value="collector">Tahsilatçı</option>
                                    <option value="admin">Yönetici</option>
                                </select>
                            </label>
                            {editId && form.role === "collector" && (
                                <label className="col-span-2 flex items-start gap-3 border border-black/15 p-3 bg-neutral-50 cursor-pointer">
                                    <input
                                        data-testid="uf-can-view-all"
                                        type="checkbox"
                                        checked={form.can_view_all_reports}
                                        onChange={(e) => setForm({ ...form, can_view_all_reports: e.target.checked })}
                                        className="mt-0.5"
                                    />
                                    <div>
                                        <div className="text-xs font-mono uppercase tracking-widest">Tüm raporları görebilir</div>
                                        <div className="text-[11px] text-neutral-500 mt-1">
                                            Bu kullanıcı diğer tahsildarların raporlarını da alabilir. Varsayılan olarak sadece kendi kayıtlarını görür.
                                        </div>
                                    </div>
                                </label>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => setOpen(false)} className="btn-outline">
                                Vazgeç
                            </button>
                            <button data-testid="uf-submit" disabled={saving} className="btn-sharp">
                                {saving ? "Kaydediliyor…" : editId ? "Güncelle" : "Kaydet"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
